export const loadedAppVersion = import.meta.env.VITE_APP_VERSION;

export function parseAppVersion(
    version: string
): { major: number; minor: number; patch: number } | null {
    const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(version.trim());
    if (!match) return null;
    return {
        major: Number(match[1]),
        minor: Number(match[2]),
        patch: Number(match[3]),
    };
}

/** True when `loaded` is strictly older than a valid, non-empty `minSupported`. */
export function isBelowMinSupportedVersion(loaded: string, minSupported: string): boolean {
    const trimmed = minSupported.trim();
    if (!trimmed || !parseAppVersion(trimmed) || !parseAppVersion(loaded)) {
        return false;
    }
    return isNewerVersion(trimmed, loaded);
}

export function isNewerVersion(latest: string, current: string): boolean {
    const latestParsed = parseAppVersion(latest);
    const currentParsed = parseAppVersion(current);
    if (!latestParsed || !currentParsed) return false;
    if (latestParsed.major !== currentParsed.major) {
        return latestParsed.major > currentParsed.major;
    }
    if (latestParsed.minor !== currentParsed.minor) {
        return latestParsed.minor > currentParsed.minor;
    }
    return latestParsed.patch > currentParsed.patch;
}

export function patchVersionsBehind(current: string, latest: string): number {
    const currentParsed = parseAppVersion(current);
    const latestParsed = parseAppVersion(latest);
    if (!currentParsed || !latestParsed) return Number.POSITIVE_INFINITY;
    if (
        currentParsed.major !== latestParsed.major ||
        currentParsed.minor !== latestParsed.minor
    ) {
        return Number.POSITIVE_INFINITY;
    }
    return latestParsed.patch - currentParsed.patch;
}

/** True when we already reloaded once trying to reach `latest` but the bundle did not update. */
export function hasReloadedForTarget(reloadTarget: string | null, latest: string): boolean {
    return reloadTarget === latest;
}

export function shouldForceAppUpdate({
    latest,
    current,
    pathname,
    forcePatchGap,
}: {
    latest: string;
    current: string;
    pathname: string;
    forcePatchGap: number;
}): boolean {
    if (pathname === "/" || pathname === "") return true;
    return patchVersionsBehind(current, latest) >= forcePatchGap;
}
